import Urls from "../../lib/Urls";
import { Code, ReturnFetchObj, apiFetch } from "../../lib/utils";
import { t } from 'i18next'
import { Invoice } from "./Invoices";
import { ProjectReport } from "../../components/modal/SubmitKickbackProject";
import { InvoicesWithCsrdAndGroupId } from "../../components/EditReports/editTypes";
import { KickbackReportPost, TKickbackProjectReport } from "./Kickback";

export interface InvoiceImportData {
  // supplier: string;
  // supplierOrgId: string;
  id:number;
  customer: string;
  customerOrgId:string;
  contractId: string;
  invoiceNumber:string;
  billingDate:string;
  rowSumExclMoms:string;
  articleNumber:string;
  articleText:string;
  articleQuantity:string;
  articleQuantityType:string;
  articlePricePerUnit:string;
  //articleOriginalPricePerUnit:string;
  groupId:number;
}

export type InvoiceKeys = "billingDate" | "customer" | "customerOrgId" | 
  "invoiceNumber" | "rowSumExclMoms" | "contractId" | "articleNumber" |
  "articleText" | "articleQuantity" | "articleQuantityType" | "articlePricePerUnit" 

export const InvoiceGroupHeaders : InvoiceKeys[] = ["billingDate", "customer", "customerOrgId", 
"invoiceNumber", "contractId"]

export type FieldStatus = "Good" | "Warning" | "Error";
export interface FieldProperty {
  key: string;
  value: string;
  validation: FieldStatus;
  message: string;
  format: string;
  type: "Number" | "Text" | "Choice";
  options?:string[];
  elevatedError?:string;
}

export interface InvoiceFields {
  id:number;
  groupId:number;
  rawArticleQuantity?: string;
  rawArticlePricePerUnit?: string;
  customer: FieldProperty;
  customerOrgId:FieldProperty;
  contractId: FieldProperty;
  invoiceNumber:FieldProperty;
  billingDate:FieldProperty;
  articleNumber:FieldProperty;
  articleText:FieldProperty;
  articleQuantity:FieldProperty;
  articleQuantityType:FieldProperty;
  articlePricePerUnit:FieldProperty;
  //articleOriginalPricePerUnit:FieldProperty;
  rowSumExclMoms:FieldProperty;
}

export interface InvoiceReport {
  id: number;
  comment: string;
  noOfInvoices: number;
  noOfInvoicesImported: number | null;
  noOfWarnings: number;
  totalSumExclMoms: number;
  totalSumExclMomsImported: number | null;
  reporter: string;
  supplierOrgId: string;
  supplierName?:string;
  reportDate: Date;
  invoicesFrom: Date;
  invoicesTo: Date;
  batchId: string;
  status: string;
  statusText: string;
  invoices?: Invoice[];
  filePath?:string;
  period:string;
  departmentId?:number;
  csrd?:boolean;
  csrdGroup?:InvoicesWithCsrdAndGroupId[];
  isProjectReport:boolean;
  kickBackProjectRows:TKickbackProjectReport[];
  
}

export interface ProjectReportEditability {
  status: 'Editable' | 'ReadOnlyIncompleteBackfill' | 'ReadOnlyLegacyUnsupported';
  reason?: string | null;
  totalProjectRows: number;
  coveredProjectRows: number;
  missingProjectRows: number;
}


const checkPeriod = (data:InvoiceReport):string => { 
  return (data.invoicesFrom.getFullYear() === data.invoicesTo.getFullYear() && data.invoicesFrom.getMonth() === data.invoicesTo.getMonth()) ?  data.invoicesFrom.getFullYear() + '-' + (data.invoicesFrom.getMonth() + 1).toString().padStart(2,"0") : `${data.invoicesFrom.toLocaleDateString('sv-SE')} - ${data.invoicesTo.toLocaleDateString('sv-SE')}`
}

const Get = async () : Promise<InvoiceReport[]> => {
  const response = await apiFetch(Urls.InvoiceReport);
  const jsonReports = await response.json();
  return jsonReports.map((item:any) => {
    const report : InvoiceReport = {
      ...item,
      reportDate : new Date(item.reportDate),
      invoicesFrom : new Date(item.invoicesFrom),
      invoicesTo : new Date(item.invoicesTo)
    }
    return report;
  })
}

const GetReportedBySupplier = async (opt?: { supplier?: string }) : Promise<InvoiceReport[]> => {
  const queryString = opt?.supplier ? `?supplier=${opt.supplier}` : "";
  const response = await apiFetch(`${Urls.InvoiceReport}/reported-by-supplier${queryString}`);
  const jsonReports = await response.json();
  return jsonReports.map((item:any) => {
    const report : InvoiceReport = {
      ...item,
      reportDate : new Date(item.reportDate),
      invoicesFrom : new Date(item.invoicesFrom),
      invoicesTo : new Date(item.invoicesTo)
    }
    return report;
  })
}

const GetById = async (id:number) => {
  const response = await apiFetch(Urls.InvoiceReport+`/${id}`);
  const jsonReport = await response.json();

  const report : InvoiceReport = {
    ...jsonReport,
    kickbackProjectRows:(jsonReport?.kickbackProjectRows?.map((x:any) => {
      return {...x,projectEndDate:x?.projectEndDate == null ? '':new Date(x?.projectEndDate)}
    } )),
    csrdGroup: (jsonReport?.csrdGroup?.map((x:any) => {
      // Backend may return PascalCase properties; normalize to the camelCase shape used in the UI.
      const billingDate = new Date(x?.billingDate ?? x?.BillingDate);
      return {
        ...x,
        groupId: x?.groupId ?? x?.GroupId,
        id: x?.id ?? x?.Id,
        invoiceId: x?.invoiceId ?? x?.InvoiceId,
        invoiceNumber: x?.invoiceNumber ?? x?.InvoiceNumber,
        supplierOrgId: x?.supplierOrgId ?? x?.SupplierOrgId,
        customerName: x?.customerName ?? x?.CustomerName,
        customerOrgId: x?.customerOrgId ?? x?.CustomerOrgId,
        contractId: x?.contractId ?? x?.ContractId,
        billingDate,
        articleNumber: x?.articleNumber ?? x?.ArticleNumber,
        articleText: x?.articleText ?? x?.ArticleText,
        articleQuantity: x?.articleQuantity ?? x?.ArticleQuantity,
        articleQuantityType: x?.articleQuantityType ?? x?.ArticleQuantityType,
        articlePricePerUnit: x?.articlePricePerUnit ?? x?.ArticlePricePerUnit,
        rowSumExclMoms: x?.rowSumExclMoms ?? x?.RowSumExclMoms,
        projectName: x?.projectName ?? x?.ProjectName,
        invoiceReference: x?.invoiceReference ?? x?.InvoiceReference,
        invoiceIdentifier: x?.invoiceIdentifier ?? x?.InvoiceIdentifier,
        uiGroupId: x?.uiGroupId ?? x?.UiGroupId,
        uiUniqueRowId: x?.uiUniqueRowId ?? x?.UiUniqueRowId,
        filePath: x?.filePath ?? x?.FilePath,
        type: x?.type ?? x?.Type,
        batchId: x?.batchId ?? x?.BatchId,
      } as InvoicesWithCsrdAndGroupId;
     })),
    reportDate : new Date(jsonReport.reportDate),
    invoicesFrom : new Date(jsonReport.invoicesFrom),
    invoicesTo : new Date(jsonReport.invoicesTo),
    invoices : (jsonReport?.invoices ?? []).map((inv:any) => ({
      ...inv,
      billingDate : new Date(inv.billingDate),
      dueDate : new Date(inv.dueDate),
      sendDate : new Date(inv.sendDate)
    }))
  }
  
  return report;

}

const GetProjectEditability = async (id: number): Promise<ProjectReportEditability> => {
  const response = await apiFetch(`${Urls.InvoiceReport}/${id}/project-editability`);
  return await response.json();
}

const Post = async (invoices : InvoiceImportData[], comment: string, noOfWarnings: number,departmentId?:number)  => {
  try {
    const res = await apiFetch(Urls.InvoiceReport, "POST", JSON.stringify({invoices, comment, noOfWarnings,departmentId}), {"Content-Type": "application/json"});
    return ReturnFetchObj(t('api.invoice.postSuccess'), Code.OK, {invoices, comment});
  } catch (error) {
    console.error(error);
    return ReturnFetchObj(t('api.invoice.postFail'), Code.ADD_ERR, {invoices, comment});
  }
}

const Delete = async (id: number) => {
  try {
    const res = await apiFetch(`${Urls.InvoiceReport}/${id}`, "DELETE");
    return ReturnFetchObj(t('api.invoice.deleteSuccess'), Code.OK, {id});
  } catch (error) {
    console.error(error);
    return ReturnFetchObj(t('api.invoice.deleteFail'), Code.DELETE_ERR, {id});
  }
}

const PublishDraft = async (id: number) => {
  try {
    await apiFetch(`${Urls.InvoiceReport}/${id}/publish`, "PUT", JSON.stringify({}), {"Content-Type": "application/json"});
    return ReturnFetchObj(t('api.invoice.updateSuccess'), Code.OK, {id});
  } catch (error) {
    console.error(error);
    return ReturnFetchObj(t('api.invoice.updateFail'), Code.UPDATE_ERR, {id});
  }
}

const GetTemplates = async () : Promise<string[]> => {
  const res = await apiFetch(Urls.InvoiceReport+"/templates");
  return res.json();
}

const GetTemplateByName = async (fileName:string) => {
  return apiFetch(Urls.InvoiceReport+"/templates/"+fileName);
}

const IsAnyInProgress = async () => {
  try {
    const res = await apiFetch(Urls.InvoiceReport+"/inprogress");
    const inProgress = await res.json();

    return ReturnFetchObj("", Code.OK, {inProgress: inProgress});
  } catch (error) {
    console.error(error);
    return ReturnFetchObj(t('api.invoice.inProgressFail'), Code.GET_ERR, {inProgress: true});
  }
  
}

export {
  checkPeriod,
  Get,
  GetReportedBySupplier,
  GetById,
  GetProjectEditability,
  Post,
  GetTemplates,
  GetTemplateByName,
  Delete,
  PublishDraft,
  IsAnyInProgress
}
